import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, DisplayL } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { ANY_SPORT_PLAY_ADVENT_CALENDAR } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useOneTimeDialogContext } from '@sorare/core/src/contexts/oneTimeDialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { useCurrentSport } from '@sorare/core/src/hooks/useCurrentSport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { NeonChristmasBackground } from '../NeonChristmasBackground';
import boostUrl from './assets/boost.png';
import cardUrl from './assets/card.png';

const Content = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit);
  text-align: center;
  height: 100%;
  justify-content: space-between;
`;

const Illustration = styled.figure`
  margin: 0;
  position: relative;
  height: 280px;
  width: 100%;
  > * {
    position: absolute;
    left: 50%;
    bottom: 40px;
    scale: 0.5;
    transform-origin: center bottom;
    filter: drop-shadow(0 0 48px var(--c-nl-900));

    &:nth-child(1) {
      translate: -45% 5%;
      scale: 0.5;
    }
    &:nth-child(2) {
      translate: -50% 10%;
      scale: 0.5;
    }

    &:nth-child(3) {
      bottom: 140px;
      translate: -15% 0;
    }
  }
`;

export const Teaser = () => {
  const {
    flags: { useNeonChristmas = false },
  } = useFeatureFlags();

  const { sawDialog } = useOneTimeDialogContext();
  const sport = useCurrentSport();
  const { currentUser } = useCurrentUserContext();
  const { update } = useLifecycle();

  if (!useNeonChristmas) {
    return null;
  }

  useEffect(() => {
    // Disable teaser for onboarded users to keep them focused
    if (
      currentUser?.forcedIntoOnboarding &&
      !sawDialog(LIFECYCLE.sawNeonChristmasStartedDialog)
    ) {
      update(LIFECYCLE.sawNeonChristmasStartedDialog, true);
    }
  }, [currentUser?.forcedIntoOnboarding, sawDialog, update]);

  return (
    <OneTimeDialog
      dialogId={LIFECYCLE.sawNeonChristmasStartedDialog}
      show={!sawDialog(LIFECYCLE.sawNeonChristmasStartedDialog)}
    >
      {({ onClose, open }) =>
        open && (
          <Dialog
            maxWidth="xs"
            fullWidth
            open={open}
            hideHeader
            contentStyle={{
              border: '1px solid var(--c-nd-100)',
              borderRadius: 'var(--unit)',
              overflow: 'clip',
            }}
          >
            <NeonChristmasBackground>
              <Content>
                <Vertical center>
                  <Illustration>
                    <img src={cardUrl} alt="" />
                    <img src={cardUrl} alt="" />
                    <img src={boostUrl} alt="" />
                  </Illustration>

                  <>
                    <DisplayL>
                      <FormattedMessage
                        id="neonChristmas.launch.title"
                        defaultMessage="Christmas is almost here"
                      />
                    </DisplayL>
                    <BodyM>
                      <FormattedMessage
                        id="neonChristmas.launch.description2"
                        defaultMessage="A new surprise everyday on Sorare"
                      />
                    </BodyM>
                  </>
                </Vertical>

                <Button
                  size="medium"
                  to={generateSportPath(ANY_SPORT_PLAY_ADVENT_CALENDAR, {
                    sport,
                  })}
                  onClick={onClose}
                  color="primary"
                  fullWidth
                >
                  <FormattedMessage
                    id="neonChristmas.launch.cta"
                    defaultMessage="Discover today's surprise"
                  />
                </Button>
                <Button
                  size="medium"
                  onClick={onClose}
                  color="transparent"
                  fullWidth
                >
                  <FormattedMessage {...glossary.later} />
                </Button>
              </Content>
            </NeonChristmasBackground>
          </Dialog>
        )
      }
    </OneTimeDialog>
  );
};
